import { Component, Input } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { DataResponse, ProfileChanges, UpdateUserProfileRequest } from "@apiserver-rest/ponder-data-client-base";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { SvgIconComponent } from "@ponder-shared/components/svg-icon.component";
import { SvgIcon } from "@ponder-shared/utils/svgIcons";
import { distinctUntilChanged, take } from "rxjs";

@Component({
    selector: "user-display-name-change",
    imports: [ReactiveFormsModule, BusySpinComponent, SvgIconComponent],
    templateUrl: "./user-account-display-name-change.component.html"
})
export class UserAccountDisplayNameChangeComponent {
    @Input({ required: true }) public authSession?: AuthSession;

    public readonly checkSvgIcon = SvgIcon.Check;

    public updateDisplayNameForm!: FormGroup;
    public isUpdatingDisplayName: boolean = false;
    public updatingDisplayNameError?: string;
    public updateSuccess?: boolean = undefined;

    private currDisplayName?: string;
    private hasDisplayNameChanged: boolean = false;

    public constructor(formBuilder: FormBuilder, private ponderDataClient: PonderDataClient, private events: EventBroker) {
        this.updateDisplayNameForm = formBuilder.group({
            displayName: ["", [Validators.maxLength(128)]]
        });

        this.updateDisplayNameForm
            .valueChanges
            .pipe(distinctUntilChanged())
            .subscribe(() => { 
                this.onDisplayNameChanged() 
            });
    }

    public ngOnChanges(): void {
        this.currDisplayName = this.authSession?.displayName;

        const formElement = this.updateDisplayNameForm.get("displayName")!;

        if (this.currDisplayName && !formElement.value) {
            formElement.setValue(this.currDisplayName);
        }
    }
    
    private onDisplayNameChanged(): void {
        this.updateSuccess = undefined;
        this.hasDisplayNameChanged = (this.currDisplayName != this.updateDisplayNameForm.get("displayName")!.value);
    }

    public canSubmitDisplayNameChange(): boolean {
        return this.updateDisplayNameForm.valid && this.hasDisplayNameChanged && !this.isUpdatingDisplayName;
    }
    
    public onChangeDisplayNameClicked(): void {
        if (!this.canSubmitDisplayNameChange()) {
            return;
        }

        this.isUpdatingDisplayName = true;
        this.updatingDisplayNameError = undefined;
        this.updateSuccess = undefined;
        this.updateDisplayNameForm.disable();

        const username = this.authSession?.username;
        const displayName = this.updateDisplayNameForm.get("displayName")!.value;

        const request = new UpdateUserProfileRequest({
            username: username,
            profileChanges: new ProfileChanges({
                displayName: displayName
            })
        });

        this.ponderDataClient
            .userProfilePatch(request)
            .pipe(take(1))
            .subscribe({
                next: (response: DataResponse) => {
                    if (response.success) {
                        this.currDisplayName = displayName;
                        this.hasDisplayNameChanged = false;
                        this.events.profile.displayNameUpdatedEvent.emit(displayName);
                        this.updateSuccess = true;
                    } else {
                        this.updatingDisplayNameError = "Failed to update display name.";
                        this.updateSuccess = false;
                    }
                },
                error: () => {
                    this.updatingDisplayNameError = "Failed to update display name.";
                    this.updateSuccess = false;
                }
            })
            .add(() => {
                this.isUpdatingDisplayName = false;
                this.updateDisplayNameForm.enable({
                    emitEvent: false        // Dont trigger onDisplayNameChanged
                });
            });
    }
}